import React, { useState, useEffect, useCallback } from 'react';
import { KeyRound, RefreshCw, ScanSearch, Check, Undo2, Loader2, Users, Package, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const PrivateCollectionAdmin = () => {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState({});
  const [compEmail, setCompEmail] = useState('');
  const [comping, setComping] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/private-collection/overview`);
      if (res.ok) setData(await res.json());
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/api/admin/private-collection/scan`, { method: 'POST' });
      const r = await res.json();
      if (!res.ok) throw new Error(r.detail || 'Scan failed');
      toast.success(`Scanned ${r.scanned} listings — ${r.flagged.length} flagged, ${r.remaining} still to scan`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setScanning(false);
    }
  };

  const act = async (id, action) => {
    setBusy(prev => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API}/api/admin/private-collection/items/${id}/${action}`, { method: 'POST' });
      if (!res.ok) throw new Error('Action failed');
      toast.success(action === 'approve' ? 'Placed in the Collection (free)' : 'Returned to the main marketplace');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(prev => ({ ...prev, [id]: false }));
    }
  };

  const comp = async () => {
    if (!compEmail.trim()) { toast.error('Enter the member\u2019s email'); return; }
    if (!window.confirm(`Gift ${compEmail.trim()} lifetime free membership + Seller Pass, and activate their pending 18+ listings?`)) return;
    setComping(true);
    try {
      const res = await fetch(`${API}/api/admin/private-collection/comp-membership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: compEmail.trim() }),
      });
      const r = await res.json();
      if (!res.ok) throw new Error(typeof r.detail === 'string' ? r.detail : 'Failed');
      toast.success(`Gifted to ${r.user} — ${r.listings_activated} listing(s) activated. They've been emailed.`);
      setCompEmail('');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setComping(false);
    }
  };

  if (!data) {
    return <div className="bg-white rounded-xl border p-8 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const rb = data.revenue_breakdown || {};

  return (
    <div className="space-y-4" data-testid="admin-pc-section">
      {/* Hero stats */}
      <div className="rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #14100e 0%, #241b16 100%)', border: '1px solid rgba(201,169,98,0.3)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ border: '1px solid rgba(201,169,98,0.5)' }}>
              <KeyRound className="w-5 h-5" style={{ color: '#c9a962' }} />
            </div>
            <div>
              <h3 className="text-lg" style={{ fontFamily: 'Georgia, serif' }}>The Private Collection</h3>
              <p className="text-xs text-white/50">18+ boutique · memberships, placements & AI content sweep</p>
            </div>
          </div>
          <div className="flex gap-5 text-center flex-wrap">
            {[['Members', data.members, 'pc-stat-members'], ['Seller Passes', data.seller_passes, 'pc-stat-passes'],
              ['Live Pieces', data.live_items_count, 'pc-stat-live'], ['Pending', data.pending_items_count, 'pc-stat-pending'],
              ['Revenue', `$${data.revenue}`, 'pc-stat-revenue']].map(([l, v, tid]) => (
              <div key={l} data-testid={tid}><p className="text-xl font-bold" style={{ color: '#c9a962', fontFamily: 'Georgia, serif' }}>{v}</p><p className="text-[9px] uppercase tracking-wide text-white/50">{l}</p></div>
            ))}
          </div>
        </div>
        <div className="flex gap-4 mt-4 pt-3 text-[11px] text-white/60 flex-wrap" style={{ borderTop: '1px solid rgba(201,169,98,0.2)' }}>
          <span>Subscriptions: <span style={{ color: '#c9a962' }}>${rb.pc_membership || 0}</span></span>
          <span>Seller Passes: <span style={{ color: '#c9a962' }}>${rb.pc_seller_pass || 0}</span></span>
          <span>Placements: <span style={{ color: '#c9a962' }}>${rb.pc_listing || 0}</span></span>
        </div>
      </div>

      {/* Sweep + gift tools */}
      <div className="bg-white rounded-xl border p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-gray-500">{data.unscanned} listings not yet AI-checked for 18+ content</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={scan} disabled={scanning || data.unscanned === 0} data-testid="admin-pc-scan-btn">
              {scanning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ScanSearch className="w-3 h-3 mr-1" />}
              {scanning ? 'Scanning…' : 'Run AI sweep'}
            </Button>
            <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="w-3 h-3" /></Button>
          </div>
        </div>
        {data.pending_items?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-700">Flagged — awaiting seller placement payment:</p>
            {data.pending_items.map(item => (
              <div key={item.id} className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid={`admin-pc-pending-${item.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 truncate">{item.title}</p>
                  <p className="text-[11px] text-amber-700">{item.adult_flag_reason} · seller: {item.seller_name}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="text-xs h-7" disabled={busy[item.id]} onClick={() => act(item.id, 'approve')} data-testid={`admin-pc-approve-${item.id}`}>
                    <Check className="w-3 h-3 mr-1" /> Place free
                  </Button>
                  <Button size="sm" variant="ghost" className="text-xs h-7" disabled={busy[item.id]} onClick={() => act(item.id, 'unflag')} data-testid={`admin-pc-unflag-${item.id}`}>
                    <Undo2 className="w-3 h-3 mr-1" /> Not 18+
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="pt-3 border-t">
          <p className="text-xs font-medium text-gray-700 mb-2">🗝️ Gift founding membership (lifetime free + Seller Pass, activates their pending pieces)</p>
          <div className="flex gap-2 max-w-md">
            <input type="email" placeholder="member@email.com" value={compEmail} onChange={e => setCompEmail(e.target.value)}
              className="flex-1 h-9 px-3 text-sm border rounded-md outline-none focus:ring-1 focus:ring-emerald-600" data-testid="admin-pc-comp-email" />
            <Button size="sm" onClick={comp} disabled={comping} className="h-9 bg-minted-600 hover:bg-minted-700" data-testid="admin-pc-comp-btn">
              {comping ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Gift membership'}
            </Button>
          </div>
          <button
            onClick={async () => {
              try {
                const res = await fetch(`${API}/api/admin/private-collection/resend-founding-email`, { method: 'POST' });
                const r = await res.json();
                if (!res.ok) throw new Error(typeof r.detail === 'string' ? r.detail : 'Failed');
                toast.success(`Founding email re-sent to ${r.sent_to}`);
              } catch (e) { toast.error(e.message); }
            }}
            className="text-[11px] text-gray-400 underline mt-2 hover:text-gray-600"
            data-testid="admin-pc-resend-founding-btn"
          >
            Re-send the founding member's lifetime-key email
          </button>
        </div>
      </div>

      {/* Members */}
      <div className="bg-white rounded-xl border overflow-hidden" data-testid="admin-pc-members">
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <h4 className="text-sm font-semibold text-gray-900">Members & Subscribers ({data.members_list?.length || 0})</h4>
        </div>
        {(!data.members_list || data.members_list.length === 0) ? (
          <p className="text-sm text-gray-400 text-center py-6">No members yet — they'll appear here as they join.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b">
                <th className="px-4 py-2">Member</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Seller Pass</th><th className="px-4 py-2">Billing</th>
              </tr></thead>
              <tbody>
                {data.members_list.map(m => (
                  <tr key={m.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`admin-pc-member-${m.id}`}>
                    <td className="px-4 py-2.5">
                      <p className="text-gray-900">{m.name || '—'}</p>
                      <p className="text-xs text-gray-400">{m.email}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      {m.pc_comp
                        ? <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#14100e', color: '#c9a962' }}><Crown className="w-3 h-3" /> Founding (free)</span>
                        : m.pc_member
                          ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">$4.99/mo</span>
                          : <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Pass only</span>}
                    </td>
                    <td className="px-4 py-2.5">{m.pc_seller_pass ? <Check className="w-4 h-4 text-emerald-600" /> : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-xs" data-testid={`admin-pc-billing-${m.id}`}>
                      {m.pc_comp
                        ? <span className="text-gray-500">Lifetime</span>
                        : m.pc_cancelled
                          ? <span className="text-red-500 font-medium">Cancelled — access ends {m.pc_member_until ? new Date(m.pc_member_until).toLocaleDateString() : ''}</span>
                          : m.pc_billing_issue
                            ? <span className="text-amber-600 font-medium">Card issue — Stripe retrying</span>
                            : m.pc_member && m.pc_member_until
                              ? <span className="text-emerald-700 font-medium">🔁 Renews {new Date(m.pc_member_until).toLocaleDateString()}</span>
                              : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Live pieces */}
      <div className="bg-white rounded-xl border overflow-hidden" data-testid="admin-pc-live-items">
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <Package className="w-4 h-4 text-gray-400" />
          <h4 className="text-sm font-semibold text-gray-900">Pieces in the Collection ({data.live_items?.length || 0})</h4>
        </div>
        {(!data.live_items || data.live_items.length === 0) ? (
          <p className="text-sm text-gray-400 text-center py-6">Nothing placed yet — run the AI sweep or wait for sellers.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b">
                <th className="px-4 py-2">Piece</th><th className="px-4 py-2">Category</th><th className="px-4 py-2">Price</th><th className="px-4 py-2">Seller</th><th className="px-4 py-2"></th>
              </tr></thead>
              <tbody>
                {data.live_items.map(p => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`admin-pc-item-${p.id}`}>
                    <td className="px-4 py-2.5 text-gray-900">{p.title}{p.sold && <span className="ml-2 text-[10px] text-gray-400 uppercase">sold</span>}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{p.category}</td>
                    <td className="px-4 py-2.5 font-medium" style={{ color: '#8a6d2f' }}>${Number(p.price).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{p.seller_name}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" variant="ghost" className="text-xs h-7 text-gray-400" disabled={busy[p.id]} onClick={() => act(p.id, 'unflag')} data-testid={`admin-pc-remove-${p.id}`}>
                        <Undo2 className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivateCollectionAdmin;
